import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { listInstalmentPlans } from "@/lib/api";
import { fmtMoneyWith } from "@/lib/money";
import { toast } from "sonner";
import { Phone } from "lucide-react";

const STATE = {
  blocked: { label: "Blocked · access paused", cls: "bg-red-500/15 text-red-300" },
  grace: { label: "Overdue · in grace days", cls: "bg-amber-500/15 text-amber-300" },
  due: { label: "Due today", cls: "bg-amber-500/10 text-amber-200" },
  due_soon: { label: "Due in 3 days", cls: "bg-sky-500/10 text-sky-300" },
  upcoming: { label: "On track", cls: "bg-emerald-500/10 text-emerald-300" },
};
const fmtDay = (d) => {
  if (!d) return "—";
  const dt = new Date(d.slice(0, 10) + "T00:00:00");
  return dt.toLocaleDateString([], { day: "numeric", month: "short", ...(dt.getFullYear() !== new Date().getFullYear() && { year: "numeric" }) });
};

/** Annual memberships being paid in 3 parts from the app. Members pay each part in the app (online
 *  or cash at the desk); the app reminds them and pauses their access 3 days after a missed date. */
export default function PaymentPlans() {
  const { activeFacility } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => listInstalmentPlans().then(setData)
    .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load payment plans"); setData({ plans: [] }); }), []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  return (
    <div className="space-y-6" data-testid="payment-plans">
      <div>
        <div className="eyebrow">Retention · Payments</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Payment Plans</h2>
        <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
          Annual memberships paid in 3 monthly parts from the app. Members pay each part in the app (online, or cash at the desk
          with your staff ID) and get reminders. Paying by the due date, or within {data?.grace_days ?? 3} days after, earns reward
          points; after that the app pauses their access until they pay. Each part shows in All Sales on the day it&apos;s paid.
        </p>
      </div>
      {data === null ? (
        <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>
      ) : data.plans.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-zinc-500">No Annual memberships are being paid in parts at this gym.</div>
      ) : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <tr className="border-b border-[#1c1c1c]">
                <th className="px-4 py-3">Member</th><th className="px-4 py-3">Paid</th><th className="px-4 py-3">Next</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.plans.map((p) => {
                const st = p.status === "done" ? { label: "Fully paid", cls: "bg-zinc-500/10 text-zinc-400" } : STATE[p.next?.state] || STATE.upcoming;
                return (
                  <tr key={p.id} className="border-b border-[#161616]">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-zinc-100">{p.name}</div>
                      {p.phone && <a href={`tel:+${p.phone}`} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white"><Phone className="h-3 w-3" />+{p.phone}</a>}
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{p.paid} of {p.count}</td>
                    <td className="px-4 py-3 text-zinc-300">
                      {p.next ? <>Part {p.next.n}: {fmtMoneyWith(p.currency_symbol, p.next.total)} <span className="text-zinc-500">· due {fmtDay(p.next.due_date)}</span></> : "—"}
                    </td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.cls}`}>{st.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
